import { useCallback, useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from "recharts";
import { api, formatErr } from "../lib/api";
import DashboardShell from "../components/DashboardShell";
import { SAR } from "../components/RiyalIcon";

const PERIODS = [
  { id: "today", label: "اليوم", salesLabel: "مبيعات اليوم" },
  { id: "week", label: "آخر 7 أيام", salesLabel: "مبيعات آخر 7 أيام" },
  { id: "month", label: "آخر 30 يوم", salesLabel: "مبيعات آخر 30 يوم" },
];

const METRICS = [
  {
    id: "sales",
    key: "sales",
    tooltipLabel: "المبيعات",
    todayTitle: "المبيعات حسب الساعة",
    periodTitle: "حركة المبيعات",
    isCurrency: true,
  },
  {
    id: "orders_count",
    key: "orders_count",
    tooltipLabel: "الطلبات",
    todayTitle: "الطلبات حسب الساعة",
    periodTitle: "حركة الطلبات",
    isCurrency: false,
  },
  {
    id: "average_order",
    key: "average_order",
    tooltipLabel: "متوسط الطلب",
    todayTitle: "متوسط الطلب حسب الساعة",
    periodTitle: "متوسط الطلب اليومي",
    isCurrency: true,
  },
];

function westernNumerals(value) {
  const easternDigits = "٠١٢٣٤٥٦٧٨٩";
  return String(value).replace(/[٠-٩]/g, (digit) => String(easternDigits.indexOf(digit)));
}

function chartLabel(date, period) {
  if (period === "today") return "اليوم";
  if (period === "month") return westernNumerals(Number(date.slice(-2)));
  return new Intl.DateTimeFormat("ar-SA", { weekday: "short" }).format(
    new Date(`${date}T12:00:00+03:00`),
  );
}

function hourLabel(hour) {
  const displayHour = hour % 12 || 12;
  return `${westernNumerals(displayHour)} ${hour < 12 ? "AM" : "PM"}`;
}

function monthDayLabel(date) {
  const [, month, day] = date.split("-");
  return `${westernNumerals(Number(month))}/${westernNumerals(Number(day))}`;
}

function ChartTooltip({ active, payload, label, metric, period }) {
  if (!active || !payload?.length) return null;
  const value = westernNumerals(payload[0].value);
  const tooltipLabel = period === "month" ? monthDayLabel(payload[0].payload.date) : label;
  return (
    <div
      className="select-none rounded-xl border border-orange-100 bg-white px-3 py-2 text-sm shadow-sm"
      data-testid="summary-chart-tooltip"
    >
      <div className="text-xs text-gray-500">{tooltipLabel}</div>
      <div className="mt-1 font-extrabold text-orange-700">
        {metric.isCurrency ? (
          <span>
            {metric.tooltipLabel}: <SAR>{value}</SAR>
          </span>
        ) : (
          `${metric.tooltipLabel}: ${value}`
        )}
      </div>
    </div>
  );
}

export default function Summary() {
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState("sales");

  const load = useCallback(async (selectedPeriod) => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/summary", { params: { period: selectedPeriod } });
      setSummary(response.data);
    } catch (requestError) {
      setError(formatErr(requestError));
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(period);
  }, [load, period]);

  const selected = PERIODS.find((item) => item.id === period) || PERIODS[0];
  const activeMetric = METRICS.find((item) => item.id === selectedMetric) || METRICS[0];
  const isToday = period === "today";
  const chartData = useMemo(
    () => {
      if (isToday) {
        return (summary?.hourly_series || []).map((item) => ({
          ...item,
          label: hourLabel(item.hour),
        }));
      }
      return (summary?.series || []).map((item) => ({
        ...item,
        label: chartLabel(item.date, period),
      }));
    },
    [isToday, period, summary],
  );

  return (
    <DashboardShell title="ملخص المبيعات">
      <div className="mb-4 grid grid-cols-3 gap-2" data-testid="summary-period-tabs">
        {PERIODS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setPeriod(item.id)}
            data-testid={`summary-period-${item.id}`}
            aria-pressed={period === item.id}
            className={`rounded-2xl border px-2 py-2.5 text-sm font-bold transition-colors ${
              period === item.id
                ? "brand-grad border-transparent text-white"
                : "border-gray-200 bg-white text-gray-700 hover:bg-orange-50"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-gray-500" data-testid="summary-loading">جاري التحميل…</div>}

      {error && (
        <div className="rounded-2xl border border-red-100 bg-red-50 p-4 text-red-700" data-testid="summary-error">
          <div className="text-sm font-bold">تعذر تحميل الملخص</div>
          <div className="mt-1 text-sm">{error}</div>
          <button
            type="button"
            onClick={() => load(period)}
            className="mt-3 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-bold"
            data-testid="summary-retry-button"
          >
            إعادة المحاولة
          </button>
        </div>
      )}

      {!loading && !error && summary && (
        <div className="grid gap-3" data-testid="summary-content">
          <Card
            label={selected.salesLabel}
            value={<SAR>{summary.sales}</SAR>}
            testid="sum-sales"
            tone="orange"
            active={activeMetric.id === "sales"}
            onClick={() => setSelectedMetric("sales")}
          />
          <Card
            label="عدد الطلبات"
            value={<span className="num-badge">{summary.orders_count}</span>}
            testid="sum-count"
            tone="amber"
            active={activeMetric.id === "orders_count"}
            onClick={() => setSelectedMetric("orders_count")}
          />
          <Card
            label="متوسط الطلب"
            value={<SAR>{summary.average_order.toFixed(1)}</SAR>}
            testid="sum-avg"
            tone="green"
            active={activeMetric.id === "average_order"}
            onClick={() => setSelectedMetric("average_order")}
          />

          <section
            className={
              "touch-pan-y select-none rounded-2xl border border-orange-100 bg-white p-4 outline-none "
              + "[&_*]:touch-pan-y [&_*]:select-none [&_*]:outline-none"
            }
            data-testid="summary-chart"
          >
            <div className="mb-3">
              <h2 className="text-base font-extrabold" data-testid="summary-chart-title">
                {isToday ? activeMetric.todayTitle : activeMetric.periodTitle}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500" data-testid="summary-chart-period">
                {selected.label}
              </p>
            </div>
            <div className="h-60" data-testid="summary-chart-data">
              <BarChart
                responsive
                data={chartData}
                style={{ width: "100%", height: "100%" }}
                margin={{ top: 8, right: 0, left: -24, bottom: 0 }}
              >
                <CartesianGrid vertical={false} stroke="#F3F4F6" />
                <XAxis
                  dataKey="label"
                  interval={isToday ? 1 : period === "month" ? 3 : 0}
                  angle={isToday ? -90 : 0}
                  textAnchor={isToday ? "end" : "middle"}
                  dx={isToday ? -4 : 0}
                  height={isToday ? 70 : 30}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#6B7280", fontSize: isToday ? 9 : 11 }}
                />
                <YAxis
                  allowDecimals={activeMetric.id === "average_order"}
                  tickFormatter={westernNumerals}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#6B7280", fontSize: 11 }}
                />
                <Tooltip
                  content={<ChartTooltip metric={activeMetric} period={period} />}
                  cursor={{ fill: "#FFF7ED" }}
                />
                <Bar
                  dataKey={activeMetric.key}
                  fill="#F97316"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={42}
                >
                  {chartData.map((item, index) => {
                    const isCurrent = isToday
                      ? item.hour === summary?.current_hour
                      : index === chartData.length - 1;
                    return (
                      <Cell
                        key={isToday ? item.hour : item.date}
                        fill={isCurrent ? "#FFFFFF" : "#F97316"}
                        stroke={isCurrent ? "#F97316" : "none"}
                        strokeWidth={isCurrent ? 2 : 0}
                      />
                    );
                  })}
                </Bar>
              </BarChart>
            </div>
          </section>
        </div>
      )}
    </DashboardShell>
  );
}

function Card({ label, value, tone, testid, active, onClick }) {
  const cls = {
    orange: "bg-orange-50 border-orange-100 text-orange-800",
    amber: "bg-amber-50 border-amber-100 text-amber-800",
    green: "bg-green-50 border-green-100 text-green-800",
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`w-full rounded-2xl border p-5 text-right transition-shadow ${cls} ${
        active ? "ring-2 ring-orange-300 ring-offset-1" : ""
      }`}
      data-testid={testid}
    >
      <div className="text-sm">{label}</div>
      <div className="text-3xl font-extrabold mt-1">{value}</div>
    </button>
  );
}
